import { Fragment, useState, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import cx from 'classnames';
import { getAllTimezones } from '../../utils/lib';
import { useProfile } from '../../hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserApi, PeopleApi } from '../../api';
import { Spinner } from '../atoms';
import { toast } from 'react-toastify';

type TimezoneOption = {
  value: string;
  label: string;
  offset: string;
};

export const TimezoneSelector: FunctionComponent = () => {
  const profile = useProfile();
  const queryClient = useQueryClient();
  const timezones = getAllTimezones();
  
  const currentTimezone = profile?.timezone || 'UTC';
  const defaultOption = timezones.find((tz) => tz.value === currentTimezone) || timezones[0];
  const [selected, setSelected] = useState<TimezoneOption>(defaultOption);

  useEffect(() => {
    if (profile?.timezone) {
      const tzOption = timezones.find((tz) => tz.value === profile.timezone);
      if (tzOption) {
        setSelected(tzOption);
      }
    }
  }, [profile?.timezone, timezones]);

  const changeTimezoneMutation = useMutation({
    mutationFn: (timezone: string) => UserApi.changeTimezone({ timezone }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PeopleApi.KEY, 'me'],
      });
      toast.success('Timezone updated successfully');
    },
    onError: () => {
      toast.error('Failed to update timezone');
      const tzOption = timezones.find((tz) => tz.value === currentTimezone);
      if (tzOption) {
        setSelected(tzOption);
      }
    },
  });

  const handleChange = (item: TimezoneOption) => {
    setSelected(item);
    changeTimezoneMutation.mutate(item.value);
  };

  return (
    <div className="w-full overflow-visible">
      <Listbox value={selected} onChange={handleChange} disabled={changeTimezoneMutation.isPending}>
        <div className="relative mt-1">
          <Listbox.Button
            className={cx(
              'relative w-full cursor-default rounded-lg bg-white p-2 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm',
              {
                'bg-blue-900/20': changeTimezoneMutation.isPending,
              }
            )}
          >
            <span className="block truncate">
              {changeTimezoneMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner />
                  {selected.label}
                </span>
              ) : (
                selected.label
              )}
            </span>
            {!changeTimezoneMutation.isPending && (
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            )}
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {timezones.map((item) => (
                <Listbox.Option
                  key={item.value}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                    }`
                  }
                  value={item}
                >
                  {({ selected: isSelected }) => (
                    <Fragment>
                      <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                        {item.label}
                      </span>
                      {isSelected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </Fragment>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

